import { useState, useMemo } from "react";
import {
  FileText,
  MapPinCheck,
  DollarSign,
  House,
  Truck,
  Wallet,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Custom SVG components
const Wechat = (props) => <img src="/wechat.webp" alt="Wechat" {...props} />;
const ALIPAY = (props) => <img src="/alipat.webp" alt="ALIPAY" {...props} />;

const services = [
  {
    icon: FileText,
    label: "Заавар сургалт",
    description: "Захиалга хийх заавар",
    color: "text-primary",
  },
  {
    icon: MapPinCheck,
    label: "Хаяг холбох",
    description: "Хятад хаяг авах",
    color: "text-yellow-600",
  },
  {
    icon: DollarSign,
    label: "Үнийн мэдээлэл",
    description: "Хүргэлтийн үнэ",
    color: "text-green-600",
  },
  {
    icon: House,
    label: "Салбарын мэдээл",
    description: "Агуулахын хаяг",
    color: "text-blue-600",
  },
  {
    icon: Truck,
    label: "Хүргэлт",
    color: "text-orange-500",
  },
  {
    icon: Wallet,
    label: "Юаны шилжүүлэг",
    color: "text-purple-500",
  },
  {
    icon: ALIPAY,
    label: "ALIPAY төлөлт",
    description: "ALIPAY төлөлт",
    color: "text-blue-500",
  },
  {
    icon: Wechat,
    label: "Wechat QR",
    description: "Wechat QR",
    color: "text-green-500",
  },
];

const ServicesSection = ({
  price = [],
  time = [],
  branch = [],
  connect = [],
  training = [],
  delivery = [],
  exchange = [],
}) => {
  const [openIndex, setOpenIndex] = useState(null);

  /* ---------------- MAP VALUES ---------------- */

  const priceMap = useMemo(() => {
    const map = {};
    price.forEach((p) => (map[p.id] = p.value));
    return map;
  }, [price]);

  const deliveryMap = useMemo(() => {
    const map = {};
    delivery.forEach((p) => (map[p.id] = p.value));
    return map;
  }, [delivery]);

  const exchangeMap = useMemo(() => {
    const map = {};
    exchange.forEach((t) => {
      map[t.id] = { name: t.exchange, value: t.value };
    });
    return map;
  }, [exchange]);

  const handleOpen = (index) => setOpenIndex(index);
  const handleClose = () => setOpenIndex(null);

  /* ---------------- POPUP CONTENT BUILDER ---------------- */

  const getDescriptionHtml = (service) => {
    /* --- Салбарын мэдээл --- */
    if (service.label === "Салбарын мэдээл" && branch.length > 0) {
      return branch
        .map(
          (b) => `
        <div style="margin-bottom:16px; padding:12px; border:1px solid #ddd; border-radius:8px;">
          <p><strong>${b.name}</strong></p>
          <p>${b.location || ""}</p>
          ${
            b.link
              ? `<p><a href="${b.link}" target="_blank" style="color:#1D4ED8;">${b.link}</a></p>`
              : ""
          }
        </div>
      `
        )
        .join("");
    }

    /* --- Үнийн мэдээлэл --- */
    if (service.label === "Үнийн мэдээлэл" && price.length > 0) {
      return priceMap[price[0].id] || service.description;
    }

    /* --- Хүргэлт --- */
    if (service.label === "Хүргэлт" && delivery.length > 0) {
      return deliveryMap[delivery[0].id] || service.description;
    }

    /* --- Юаны шилжүүлэг --- */
    if (service.label === "Юаны шилжүүлэг" && exchange.length > 0) {
      const item = exchangeMap[exchange[0].id] || {
        name: "Мэдээлэл байхгүй",
        value: "",
      };

      return `
        <div>
          <p style="font-size:16px; font-weight:600; margin-bottom:8px;">${item.name}</p>
          <p style="font-size:20px; color:#1D4ED8; font-weight:bold;">${item.value}</p>
        </div>
      `;
    }

    /* --- Заавар сургалт --- */
    if (service.label === "Заавар сургалт" && training.length > 0) {
      return training
        .map(
          (t) => `
        <div 
          style="
            margin-bottom:16px; 
            padding:12px; 
            border:1px solid #ddd; 
            border-radius:8px; 
            background:#fafafa;
          "
        >
          <p style="margin:0 0 6px 0; font-weight:600; color:#111827;">
            ${t.name || "Нэр байхгүй"}
          </p>

          ${
            t.link
              ? `<a href="${t.link}" target="_blank" style="color:#1D4ED8;">
                   Сургалтын линк рүү очих 
                 </a>`
              : `<span style="color:#6B7280;">Мэдээлэл байхгүй</span>`
          }
        </div>
      `
        )
        .join("");
    }

    /* --- ALIPAY --- */
    if (service.label === "ALIPAY төлөлт") {
      return `
        <div style="text-align:center;">
          <img src="/alipayqr.jpg" style="width:200px; border-radius:10px;" />
        </div>`;
    }

    /* --- Wechat QR --- */
    if (service.label === "Wechat QR") {
      return `
        <div style="text-align:center;">
          <img src="/wechatqr.jpg" style="width:200px; border-radius:10px;" />
        </div>`;
    }

    /* --- Хаяг холбох --- */
    if (service.label === "Хаяг холбох" && connect.length > 0) {
      return connect
        .map(
          (c) => `
      <div
        style="
          margin-bottom:18px;
          padding:12px;
          border:1px solid #d1d5db;
          border-radius:10px;
          background:#fafafa;
        "
      >
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
          <p style="margin:0; font-weight:600; font-size:16px;">${c.branchNameData}</p>
          
        </div>
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
          <p style="margin:0; font-weight:600; font-size:16px;">${c.name}</p>
          <button onclick="navigator.clipboard.writeText('${c.name}')" 
            style="padding:4px 10px; background:#1d4ed8; color:white; border-radius:5px; border:none; font-size:12px;">
            Хуулах
          </button>
        </div>

        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
          <p style="margin:0; font-weight:600;">Хаяг:</p>
          <p style="margin:0; flex:1; padding-left:10px;">${c.location}</p>
          <button onclick="navigator.clipboard.writeText('${c.location}')" 
            style="padding:4px 10px; background:#1d4ed8; color:white; border-radius:5px; border:none; font-size:12px;">
            Хуулах
          </button>
        </div>

        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
          <p style="margin:0; font-weight:600;">Утас:</p>
          <p style="margin:0; padding-left:10px;">${c.phone}</p>
          <button onclick="navigator.clipboard.writeText('${c.phone}')" 
            style="padding:4px 10px; background:#1d4ed8; color:white; border-radius:5px; border:none; font-size:12px;">
            Хуулах
          </button>
        </div>

        ${
          c.price
            ? `
          <div style="display:flex; justify-content:space-between; align-items:center;">
            <p style="margin:0; font-weight:600;">Бүс нутаг:</p>
            <p style="margin:0; padding-left:10px;">${c.price}</p>
            <button onclick="navigator.clipboard.writeText('${c.price}')" 
              style="padding:4px 10px; background:#1d4ed8; color:white; border-radius:5px; border:none; font-size:12px;">
              Хуулах
            </button>
          </div>
        `
            : ""
        }
      </div>
    `
        )
        .join("");
    }

    return service.description;
  };

  /* ---------------- UI ---------------- */

  return (
    <section id="services" className="py-16 bg-cargo-light">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12">
          Манай үйлчилгээ
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={service.label}
                onClick={() => handleOpen(index)}
                className="flex flex-col items-center bg-white rounded-xl shadow-md hover:shadow-lg transition p-6 cursor-pointer"
              >
                <Icon className={`w-10 h-10 mb-4 ${service.color}`} />
                <span className="text-lg font-semibold">{service.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {openIndex !== null && (
        <Dialog open onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle className="flex justify-between items-center">
            <span className="font-bold">{services[openIndex].label}</span>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <div
              className="text-sm"
              dangerouslySetInnerHTML={{
                __html: getDescriptionHtml(services[openIndex]),
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};

export default ServicesSection;
